import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useExportTemplates(workspaceId: string) {
  return useQuery({
    queryKey: ['export-templates', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('export_templates')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useCreateExportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: any) => {
      const { data, error } = await supabase
        .from('export_templates')
        .insert(template)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-templates'] });
      toast.success('Export template created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create template: ' + error.message);
    }
  });
}

export function useGenerateExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      templateId: string;
      billIds: string[];
      workspaceId: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('generate-accounting-export', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bill-exports'] });
      toast.success(`Export generated (${data.billCount} bills)`);
    },
    onError: (error: Error) => {
      toast.error('Export failed: ' + error.message);
    }
  });
}

export function useBillExports(billId?: string) {
  return useQuery({
    queryKey: ['bill-exports', billId],
    queryFn: async () => {
      let query = supabase
        .from('bill_exports')
        .select('*');

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      const { data, error } = await query.order('at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}
